import RatingStars from '../common/RatingStars';
import { Button } from '../common/Button';
import { FreelancerProfile } from '../../types/profile';
import { useTranslation } from 'react-i18next';
import ProfileAvatar from '../common/ProfileAvatar';
import { MapPin, MessageSquare } from 'lucide-react';
import { useHasPermission } from '../../hooks/useHasPermission';
import { useState } from 'react';
import RequestQuoteModal from '../jobs/RequestQuoteModal';
import { useProfileStore } from '../../stores/profileStore';
import { mapProfileToFreelancerSearchResult } from '../../utils/freelancerUtils';
import { canRequestAlliance } from '../../utils/allianceUtils';
import AllianceRequestModal from '../alliances/AllianceRequestModal';
import { useAuth } from '../../context/auth';
import { useNavigate } from 'react-router-dom';
import ReportModal from '../reports/ReportModal';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import { useProfileRole } from '../../hooks/useProfileRole';
import UpdateProfileStatusModal from '../UpdateProfileStatusModal';
import ProfileBioText from '../common/ProfileBioText';

interface ProfileSummaryCardProps {
  profile: FreelancerProfile;
  className?: string;
}

const ProfileSummaryCard = ({ profile, className }: ProfileSummaryCardProps) => {
  const { t } = useTranslation(['profile', 'common', 'freelancers']);
  const canRequestQuote = useHasPermission('request_quote');
  const [isQuoteRequestModalOpen, setIsQuoteRequestModalOpen] = useState(false);

  const { profile: currentProfile } = useProfileStore();
  const [isAllianceRequestModalOpen, setIsAllianceRequestModalOpen] = useState(false);
  const hasAlliancePermission = useHasPermission('request_alliance');

  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canRequestAllianceFlag = canRequestAlliance(
    profile.alliance_status,
    currentProfile?.id,
    profile.id,
    hasAlliancePermission
  );

  const profileAsSearchResult = mapProfileToFreelancerSearchResult(profile);
  const { user } = useAuth();
  const languages =
    profile.languages?.map(l => t(`language.${l}`, { ns: 'common' })).join(', ') || '—';
  const navigate = useNavigate();

  const { isAdmin } = useProfileRole();

  const cta = canRequestQuote
    ? {
        label: t('requestQuote'),
        onClick: () => setIsQuoteRequestModalOpen(true),
      }
    : canRequestAllianceFlag
      ? {
          label: t('requestAlliance'),
          onClick: () => setIsAllianceRequestModalOpen(true),
        }
      : user?.id === mapProfileToFreelancerSearchResult(profile)?.id
        ? {
            label: t('editProfile'),
            onClick: () => navigate('/settings'),
          }
        : isAdmin
          ? {
              label: t('update_status_button', {
                ns: 'common',
                defaultValue: 'Modifier le statut',
              }),
              onClick: () => setShowUpdateStatusModal(true),
            }
          : null;

  const mappedProfile = mapProfileToFreelancerSearchResult(profile);
  const allianceCount =
    mappedProfile.alliance_count > 10 ? '10+' : mappedProfile.alliance_count || 0;

  return (
    <>
      <div
        className={`bg-white rounded-2xl border-2 shadow-xl border-gray-300 flex flex-col px-4 mb-6 ${className || ''}`}
      >
        <div className="flex flex-col md:flex-row md:gap-8 md:p-8">
          <ProfileAvatar
            profile_id={profile.id}
            first_name={profile.first_name}
            avatar_url={profile.avatar_url}
            className="w-48 h-48 text-8xl mb-4 self-center md:flex-shrink-0 transition-all duration-300"
          />
          <div className="md:flex-shrink-1 md:flex md:w-full md:flex-col md:justify-between">
            <div className="mb-4 grid w-full grid-cols-5 items-start gap-2 text-secondary">
              <div className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1 text-[12px]">
                  <MapPin className="h-3 w-3 flex-shrink-0" />

                  <span>{t('location', { defaultValue: 'Ville' })}</span>
                </span>
                <span className="break-words text-sm font-bold leading-tight">
                  {profile.city?.name || '—'}
                </span>
              </div>

              <div className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1 text-[12px]">
                  <MessageSquare className="h-3 w-3 flex-shrink-0" />
                  <span>{t('languages', { defaultValue: 'Langues' })}</span>
                </span>
                <span className="break-words text-sm font-bold leading-tight">{languages}</span>
              </div>

              <div className="flex min-w-0 flex-col">
                <span className="text-[12px]">
                  {t('rating', { ns: 'freelancers', defaultValue: 'Évaluation' })}
                </span>
                <div className="flex flex-wrap items-center gap-1">
                  <RatingStars rating={profile.stats.overall_rating} starClassName={'h-3 w-3'} />
                  <span className="text-[10px]">({profile.stats.reviews_count || 0})</span>
                </div>
              </div>

              <div className="flex min-w-0 flex-col">
                <span className="text-[12px]">{t('alliances', { ns: 'freelancers' })}</span>
                <div className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary">
                  <span className="text-[10px] font-bold text-white">{allianceCount}</span>
                </div>
              </div>

              <div className="flex min-w-0 flex-col">
                <span className="text-[12px]">{t('completedProjects')}</span>
                <span className="text-sm font-bold">{profile.stats.projects_count || 0}</span>
              </div>
            </div>
            <p className="break-words">
              {profile.bio ? <ProfileBioText bio={profile.bio} profileId={profile.id} /> : '—'}
            </p>
            <div className="mt-6 flex flex-row justify-between gap-4 md:justify-end">
              <div>
                {cta && (
                  <Button className="mb-4 flex flex-shrink" onClick={cta.onClick}>
                    {cta.label}
                  </Button>
                )}
              </div>

              <div className="pl-2 md:my-3">
                <ReportButtonMenu
                  onReport={() => {
                    setShowReportModal(true);
                  }}
                  entityOwner={profile.id}
                  onCopySuccess={handleCopySuccess}
                  contentTypeTitle="report_this_user"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        {isQuoteRequestModalOpen && (
          <RequestQuoteModal
            onClose={() => setIsQuoteRequestModalOpen(false)}
            targetedFreelancer={profile}
          />
        )}
        {isAllianceRequestModalOpen && (
          <AllianceRequestModal
            freelancer={profileAsSearchResult}
            onClose={() => setIsAllianceRequestModalOpen(false)}
          />
        )}

        {showReportModal && profile && (
          <ReportModal
            entityId={profile.id}
            reportedId={profile.id}
            contentType="profile"
            onClose={() => setShowReportModal(false)}
          />
        )}

        {showUpdateStatusModal && (
          <UpdateProfileStatusModal
            user={profile}
            onClose={() => setShowUpdateStatusModal(false)}
          />
        )}

        {copied && <BottomSuccessMessage />}
      </div>
    </>
  );
};

export default ProfileSummaryCard;
